/**
 * OrdersView - Main view for managing orders
 */
import React, { useEffect, useState } from 'react';
import { useOrderStore, useProjectStore } from '../../stores';
import { useAuthStore } from '../../stores/authStore';
import type { Order, OrderStatus } from '../../types';
import { NewOrderDialog } from './NewOrderDialog';
import { EditOrderDialog } from './EditOrderDialog';
import { ApproveOrderDialog } from './ApproveOrderDialog';
import { RejectOrderDialog } from './RejectOrderDialog';
import { OrderDetailDialog } from './OrderDetailDialog';
import ConvertOrderDialog from './ConvertOrderDialog';
import './OrdersView.css';

export const OrdersView: React.FC = () => {
  const { orders, stats, isLoading, fetchOrders, fetchStats, deleteOrder } = useOrderStore();
  const { hasModule } = useAuthStore();
  const { projects, fetchProjects } = useProjectStore();
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'all'>('all');
  const [projectFilter, setProjectFilter] = useState<number | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewOrderDialog, setShowNewOrderDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [selectedOrders, setSelectedOrders] = useState<Set<number>>(new Set());
  const [bulkMode, setBulkMode] = useState(false);

  useEffect(() => {
    fetchOrders();
    fetchStats();
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  const handleFilterChange = async (status: OrderStatus | 'all') => {
    setStatusFilter(status);
    await fetchOrders(undefined, undefined, status === 'all' ? undefined : status);
  };

  const handleApprove = (order: Order) => {
    setSelectedOrder(order);
    setShowApproveDialog(true);
  };

  const handleReject = (order: Order) => {
    setSelectedOrder(order);
    setShowRejectDialog(true);
  };

  const handleDialogSuccess = async () => {
    await fetchOrders();
    await fetchStats();
  };

  const handleShowDetail = (order: Order) => {
    setSelectedOrder(order);
    setShowDetailDialog(true);
  };

  const handleEdit = (order: Order) => {
    setSelectedOrder(order);
    setShowEditDialog(true);
  };

  const handleConvert = (order: Order) => {
    setSelectedOrder(order);
    setShowConvertDialog(true);
  };

  const getProjectName = (projectId?: number) => {
    if (!projectId) return null;
    const project = projects.find(p => p.id === projectId);
    return project?.name || `ID: ${projectId}`;
  };

  const toggleOrderSelection = (orderId: number) => {
    const newSelected = new Set(selectedOrders);
    if (newSelected.has(orderId)) {
      newSelected.delete(orderId);
    } else {
      newSelected.add(orderId);
    }
    setSelectedOrders(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedOrders.size === filteredOrders.filter(o => o.status === 'pending').length) {
      setSelectedOrders(new Set());
    } else {
      const pendingIds = filteredOrders.filter(o => o.status === 'pending').map(o => o.id);
      setSelectedOrders(new Set(pendingIds));
    }
  };

  const handleBulkApprove = async () => {
    if (selectedOrders.size === 0) return;

    const projectId = window.prompt('Zadajte ID projektu pre hromadné schválenie:');
    if (!projectId) return;

    try {
      const { approveOrder } = useOrderStore.getState();
      const orderIds = Array.from(selectedOrders);
      for (let i = 0; i < orderIds.length; i++) {
        await approveOrder(orderIds[i], parseInt(projectId));
      }
      setSelectedOrders(new Set());
      setBulkMode(false);
      await handleDialogSuccess();
    } catch (error) {
      console.error('Error bulk approving orders:', error);
    }
  };

  const handleExportCSV = () => {
    // Prepare CSV data
    const headers = [
      'Číslo objednávky',
      'Dátum',
      'Status',
      'Typ zákazníka',
      'Meno/Firma',
      'Email',
      'Telefón',
      'Adresa',
      'Mesto',
      'PSČ',
      'Typ služby',
      'Rýchlosť',
      'Projekt',
      'Poznámky zákazníka'
    ];

    const rows = filteredOrders.map(order => [
      order.order_number,
      new Date(order.order_date).toLocaleDateString('sk-SK'),
      order.status === 'pending' ? 'Čakajúca' :
        order.status === 'approved' ? 'Schválená' :
        order.status === 'rejected' ? 'Zamietnutá' : 'Konvertovaná',
      order.customer_type === 'individual' ? 'Fyzická osoba' : 'Firma',
      getCustomerName(order),
      order.email,
      order.phone,
      order.address,
      order.city,
      order.zip_code,
      order.service_type === 'internet' ? 'Internet' :
        order.service_type === 'internet_tv' ? 'Internet + TV' : 'Internet + TV + Telefón',
      order.requested_speed || '',
      getProjectName(order.assigned_project_id) || '',
      order.customer_notes || ''
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `objednavky_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = async (orderId: number) => {
    if (window.confirm('Naozaj zmazať túto objednávku?')) {
      try {
        await deleteOrder(orderId);
        await fetchStats();
      } catch (error) {
        console.error('Error deleting order:', error);
      }
    }
  };

  const getStatusBadge = (status: OrderStatus) => {
    const badges = {
      pending: { icon: '🟡', text: 'Čaká', class: 'status-pending' },
      approved: { icon: '🟢', text: 'Schválené', class: 'status-approved' },
      rejected: { icon: '🔴', text: 'Zamietnuté', class: 'status-rejected' },
      converted: { icon: '✅', text: 'Konvertované', class: 'status-converted' },
    };
    const badge = badges[status];
    return <span className={`status-badge ${badge.class}`}>{badge.icon} {badge.text}</span>;
  };

  const getCustomerName = (order: Order) => {
    if (order.customer_type === 'individual') {
      return `${order.first_name} ${order.last_name}`;
    }
    return order.company_name || 'N/A';
  };

  const filteredOrders = orders.filter(order => {
    // Status filter
    if (statusFilter !== 'all' && order.status !== statusFilter) {
      return false;
    }

    // Project filter
    if (projectFilter !== 'all' && order.assigned_project_id !== projectFilter) {
      return false;
    }

    // Search filter
    const matchesSearch = searchQuery === '' ||
      getCustomerName(order).toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.phone.includes(searchQuery) ||
      order.order_number.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesSearch;
  });

  return (
    <div className="orders-view">
      <div className="orders-header">
        <div className="orders-header-left">
          <h1>📝 Objednávky</h1>
          {stats && (
            <div className="orders-stats">
              <span className="stat">Celkom: {stats.total}</span>
              <span className="stat pending">Čaká: {stats.pending}</span>
              <span className="stat approved">Schválené: {stats.approved}</span>
              <span className="stat rejected">Zamietnuté: {stats.rejected}</span>
              <span className="stat converted">Konvertované: {stats.converted}</span>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {bulkMode && selectedOrders.size > 0 && (
            <>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#667eea' }}>
                Vybraných: {selectedOrders.size}
              </span>
              <button
                onClick={handleBulkApprove}
                style={{
                  padding: '10px 20px',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                ✓ Hromadne schváliť
              </button>
              <button
                onClick={() => {
                  setSelectedOrders(new Set());
                  setBulkMode(false);
                }}
                style={{
                  padding: '10px 20px',
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                ✕ Zrušiť výber
              </button>
            </>
          )}
          {!bulkMode && (
            <button
              onClick={() => setBulkMode(true)}
              style={{
                padding: '12px 24px',
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              ☑️ Hromadný výber
            </button>
          )}
          <button
            className="btn-export"
            onClick={handleExportCSV}
            style={{
              padding: '12px 24px',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = '#059669'}
            onMouseOut={(e) => e.currentTarget.style.background = '#10b981'}
          >
            📊 Export CSV
          </button>
          <button className="btn-primary" onClick={() => setShowNewOrderDialog(true)}>
            + Nová objednávka
          </button>
        </div>
      </div>

      <div className="orders-filters">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Hľadať podľa mena, emailu, telefónu, čísla objednávky..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="project-filter">
          <select
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
            style={{
              padding: '10px 12px',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              backgroundColor: 'white',
              cursor: 'pointer',
              minWidth: '200px'
            }}
          >
            <option value="all">🏘️ Všetky projekty</option>
            {projects.map(project => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-buttons">
          <button 
            className={statusFilter === 'all' ? 'active' : ''}
            onClick={() => handleFilterChange('all')}
          >
            Všetky
          </button>
          <button 
            className={statusFilter === 'pending' ? 'active' : ''}
            onClick={() => handleFilterChange('pending')}
          >
            Čakajúce
          </button>
          <button 
            className={statusFilter === 'approved' ? 'active' : ''}
            onClick={() => handleFilterChange('approved')}
          >
            Schválené
          </button>
          <button 
            className={statusFilter === 'rejected' ? 'active' : ''}
            onClick={() => handleFilterChange('rejected')}
          >
            Zamietnuté
          </button>
          <button 
            className={statusFilter === 'converted' ? 'active' : ''}
            onClick={() => handleFilterChange('converted')}
          >
            Konvertované
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="orders-loading">Načítavam...</div>
      ) : (
        <>
          {filteredOrders.length > 0 && (
            <div style={{
              padding: '12px 32px',
              background: 'white',
              borderBottom: '1px solid #e2e8f0',
              fontSize: '14px',
              color: '#64748b',
              fontWeight: '600',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>
                Zobrazených: {filteredOrders.length} {filteredOrders.length !== orders.length && `z ${orders.length}`}
              </span>
              {bulkMode && filteredOrders.filter(o => o.status === 'pending').length > 0 && (
                <button
                  onClick={toggleSelectAll}
                  style={{
                    padding: '6px 12px',
                    background: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {selectedOrders.size === filteredOrders.filter(o => o.status === 'pending').length
                    ? '☐ Zrušiť všetky'
                    : '☑ Vybrať všetky čakajúce'}
                </button>
              )}
            </div>
          )}
          <div className="orders-list">
            {filteredOrders.length === 0 ? (
              <div className="orders-empty">
                <p>Žiadne objednávky</p>
              </div>
            ) : (
            filteredOrders.map(order => (
              <div key={order.id} className="order-card" style={{
                border: bulkMode && selectedOrders.has(order.id) ? '3px solid #667eea' : undefined
              }}>
                <div className="order-card-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {bulkMode && order.status === 'pending' && (
                      <input
                        type="checkbox"
                        checked={selectedOrders.has(order.id)}
                        onChange={() => toggleOrderSelection(order.id)}
                        style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                      />
                    )}
                    <div className="order-number">#{order.order_number}</div>
                  </div>
                  {getStatusBadge(order.status)}
                </div>
                
                <div className="order-card-body">
                  <div className="order-customer">
                    {order.customer_type === 'individual' ? '👤' : '🏢'} 
                    <strong>{getCustomerName(order)}</strong>
                  </div>
                  
                  <div className="order-details">
                    <div className="order-detail">
                      <span className="label">📍 Adresa:</span>
                      <span>{order.address}, {order.city}</span>
                    </div>
                    <div className="order-detail">
                      <span className="label">📞 Telefón:</span>
                      <span>{order.phone}</span>
                    </div>
                    <div className="order-detail">
                      <span className="label">✉️ Email:</span>
                      <span>{order.email}</span>
                    </div>
                    <div className="order-detail">
                      <span className="label">📦 Služba:</span>
                      <span>{order.service_type} {order.requested_speed && `- ${order.requested_speed}`}</span>
                    </div>
                    <div className="order-detail">
                      <span className="label">📅 Objednané:</span>
                      <span>{new Date(order.order_date).toLocaleDateString('sk-SK')}</span>
                    </div>
                    {order.assigned_project_id && (
                      <div className="order-detail">
                        <span className="label">🏘️ Projekt:</span>
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: '#e3f2fd',
                          borderRadius: '4px',
                          fontSize: '0.85rem',
                          fontWeight: 'bold'
                        }}>
                          {getProjectName(order.assigned_project_id)}
                        </span>
                      </div>
                    )}
                  </div>

                  {order.customer_notes && (
                    <div className="order-notes">
                      <strong>Poznámka zákazníka:</strong> {order.customer_notes}
                    </div>
                  )}
                </div>

                <div className="order-card-actions">
                  <button className="btn-detail" onClick={() => handleShowDetail(order)}>
                    Detail
                  </button>

                  {order.status !== 'converted' && (
                    <button className="btn-edit" onClick={() => handleEdit(order)}>
                      ✏️ Upraviť
                    </button>
                  )}

                  {/* Schvaľovanie má vlastný modul. Bez neho sa tlačidlá ani
                      neukážu — doteraz ich videl každý a po kliknutí dostal 403. */}
                  {order.status === 'pending' && hasModule('order_approval', 'edit') && (
                    <>
                      <button className="btn-approve" onClick={() => handleApprove(order)}>
                        Schváliť
                      </button>
                      <button className="btn-reject" onClick={() => handleReject(order)}>
                        Zamietnuť
                      </button>
                    </>
                  )}

                  {order.status === 'approved' && (
                    <button className="btn-convert" onClick={() => handleConvert(order)}>
                      🔄 Konvertovať
                    </button>
                  )}

                  {order.status !== 'converted' && (
                    <button className="btn-delete" onClick={() => handleDelete(order.id)}>
                      Zmazať
                    </button>
                  )}
                </div>
              </div>
            ))
            )}
          </div>
        </>
      )}

      {/* Dialogs */}
      <NewOrderDialog
        isOpen={showNewOrderDialog}
        onClose={() => setShowNewOrderDialog(false)}
      />

      <ApproveOrderDialog
        isOpen={showApproveDialog}
        order={selectedOrder}
        onClose={() => {
          setShowApproveDialog(false);
          setSelectedOrder(null);
        }}
        onSuccess={handleDialogSuccess}
      />

      <RejectOrderDialog
        isOpen={showRejectDialog}
        order={selectedOrder}
        onClose={() => {
          setShowRejectDialog(false);
          setSelectedOrder(null);
        }}
        onSuccess={handleDialogSuccess}
      />

      <OrderDetailDialog
        isOpen={showDetailDialog}
        order={selectedOrder}
        onClose={() => {
          setShowDetailDialog(false);
          setSelectedOrder(null);
        }}
      />

      <EditOrderDialog
        isOpen={showEditDialog}
        order={selectedOrder}
        onClose={() => {
          setShowEditDialog(false);
          setSelectedOrder(null);
        }}
        onSuccess={handleDialogSuccess}
      />

      {selectedOrder && (
        <ConvertOrderDialog
          order={selectedOrder}
          open={showConvertDialog}
          onClose={() => {
            setShowConvertDialog(false);
            setSelectedOrder(null);
          }}
        />
      )}
    </div>
  );
};

